// Central tools registry: one entry per concrete tool with stable handler references
// IMPORTANT: Keep handlers thin and reference existing services only to avoid breakage.

import { pricingScenarioService, profitMarginService, breakevenService, cashFlowService, pricingStrategyService } from "../services/financeService";
import { logoService } from "../services/logoService";
import { ImageGenerationService } from "../services/imageGenerationService";
import { socialSchedulerService } from "../services/socialSchedulerService";
import { SocialMediaPlannerService } from "../services/socialMediaPlannerService";
import emailService from "../services/emailService";
import { CRM_ADVISOR_TOOLS_ENABLED } from "../config/featureFlags";
import { fetchDashboardSummary } from "../services/crm/dashboardService";
import dealsService from "../services/crm/dealsService";
import contactsService from "../services/crm/contactsService";
import companiesService from "../services/crm/companiesService";
import organizationsService from "../services/crm/organizationsService";
import * as telemarketingService from "../services/crm/telemarketingService";
import schedulingService from "../services/schedulingService";
import { useCrmOrgStore } from "../store/crmOrg.store";
import { buildAdvisorDashboardSnapshot, getAtRiskDeals, buildContactDataQuality, buildCompanyDataQuality } from "./crmAdvisorTools";

export type ToolHandler = (input: any) => Promise<any>;

export interface SlotDef {
  name: string;
  prompt: string;
  required: boolean;
}

const EXPLANATUM_ENABLED_STAGES = [
  'pattern_interrupt',
  'context_reason',
  'qualification',
  'discovery',
  'problem_deep_dive',
  'micro_approvals',
  'solution_linking',
  'objection_handling',
  'commitment',
  'wrap_up'
];

export interface ToolEntry {
  id: string; // e.g., finance.breakeven
  name: string;
  description: string;
  triggers: string[]; // simple keyword seeds; matcher may augment
  inputSchema?: Record<string, any>; // minimal typing for now
  preconditions?: string[]; // auth, plan, etc.
  handler?: ToolHandler; // defined only when we have a concrete service method
  route?: string; // optional route for UI CTA
  // Optional conversational slots for multi-turn collection in chat/WhatsApp
  slots?: SlotDef[];
}

const toolsRegistryBase: ToolEntry[] = [
  // Marketing/Creative
  {
    id: "marketing.logo.generate",
    name: "Logo Generator",
    description: "Generate logos from a brief using the marketing backend.",
    triggers: ["logo", "brandmark", "generate logo", "create logo"],
    inputSchema: { prompt: "string", size: { type: "number", enum: [512, 768, 1024, 2048] } },
    slots: [
      { name: "prompt", prompt: "What kind of logo would you like? Describe the style and text.", required: true },
      { name: "size", prompt: "Preferred size? 512, 768, 1024 or 2048 (default 1024).", required: false },
    ],
    handler: async (input: { prompt: string; size?: number }) => {
      const size = input.size ?? 1024;
      return logoService.generate({ prompt: input.prompt, size });
    },
    route: "/marketing/brand-kit-creator#logo-design"
  },
  // Communications: Direct Email Sender (Gmail/IMAP via backend)
  {
    id: "communications.email.send",
    name: "Email Sender",
    description: "Send an email via your connected Gmail or SMTP account.",
    triggers: [
      "email",
      "send email",
      "compose email",
      "write email",
      "email to",
      "gmail",
      "send message",
      "outreach email"
    ],
    inputSchema: {
      to: "string",
      subject: "string?",
      text: "string?",
      html: "string?",
      fromName: "string?"
    },
    slots: [
      { name: "to", prompt: "Who should I send the email to? Please provide the email address.", required: true },
      { name: "subject", prompt: "What's the subject line?", required: false },
      { name: "text", prompt: "What would you like the email to say?", required: true },
    ],
    handler: async (input: { to: string; subject?: string; text?: string; html?: string; fromName?: string }) => {
      const to = String(input?.to || "").trim();
      const subject = typeof input?.subject === "string" ? input.subject : undefined;
      const text = typeof input?.text === "string" ? input.text : undefined;
      const html = typeof input?.html === "string" ? input.html : undefined;
      const fromName = typeof input?.fromName === "string" ? input.fromName : undefined;

      if (!to) throw new Error("Recipient 'to' is required");
      if (!text && !html) throw new Error("Either 'text' or 'html' content is required");

      return emailService.sendEmail({ to, subject, text, html, fromName });
    },
    // No route required; executed directly as a tool. If needed, UI can still open Connections to link Gmail
    // route: "/account?tab=connections"
  },
  {
    id: "marketing.images.text_to_image",
    name: "Text to Image Generator",
    description: "Generate images from text prompts.",
    triggers: ["text to image", "generate image", "create image", "ai image"],
    inputSchema: { prompt: "string", width: "number?", height: "number?", enhancePrompt: "boolean?" },
    slots: [
      { name: "prompt", prompt: "Describe the image you want me to generate.", required: true },
      { name: "width", prompt: "Optional: width in pixels (e.g., 1024).", required: false },
      { name: "height", prompt: "Optional: height in pixels (e.g., 1024).", required: false },
    ],
    handler: async (input: { prompt: string; width?: number; height?: number; enhancePrompt?: boolean }) => {
      return ImageGenerationService.generateImage(input.prompt, {
        width: input.width,
        height: input.height,
        enhancePrompt: input.enhancePrompt,
      });
    },
    route: "/marketing/text-to-image-generator"
  },

  // Marketing: Business Name Generator
  {
    id: "marketing.business_name",
    name: "Business Name Generator",
    description: "Generate creative and memorable business names based on industry, values, and target audience.",
    triggers: ["business name", "name ideas", "brand name", "company name", "startup name"],
    inputSchema: { industry: "string?", keywords: "string[]?", tone: "string?" },
    slots: [
      { name: "industry", prompt: "What industry are you in?", required: false },
      { name: "keywords", prompt: "Any keywords or themes to include? (comma-separated)", required: false },
      { name: "tone", prompt: "Preferred tone (e.g., professional, playful)?", required: false },
    ],
    route: "/marketing/business-name-generator"
  },

  // Marketing: LinkedIn Profile Enhancer (personal)
  {
    id: "marketing.linkedin_profile",
    name: "LinkedIn Profile Enhancer",
    description: "Optimize your LinkedIn profile with summaries, headlines, and experience bullets.",
    triggers: ["linkedin profile", "linkedin headline", "linkedin summary", "experience bullets"],
    inputSchema: { role: "string?", achievements: "string[]?", tone: "string?" },
    slots: [
      { name: "role", prompt: "What role or title do you want to highlight?", required: false },
      { name: "achievements", prompt: "List any achievements to include (comma-separated).", required: false },
      { name: "tone", prompt: "Preferred tone (e.g., professional, friendly)?", required: false },
    ],
    route: "/marketing/linkedin-profile-enhancer"
  },

  // Marketing: LinkedIn Company Profile Enhancer
  {
    id: "marketing.linkedin_company_profile",
    name: "LinkedIn Business Profile Enhancer",
    description: "Optimize your company's LinkedIn page with AI-generated content.",
    triggers: ["linkedin company", "company page", "company description", "about company"],
    inputSchema: { industry: "string?", companySize: "string?", tone: "string?" },
    slots: [
      { name: "industry", prompt: "What industry is your company in?", required: false },
      { name: "companySize", prompt: "What is your company size?", required: false },
      { name: "tone", prompt: "Preferred tone for the company page?", required: false },
    ],
    route: "/marketing/linkedin-business-profile-enhancer"
  },

  // Marketing: Social Media Post Generator
  {
    id: "marketing.social_posts",
    name: "Social Media Post Generator",
    description: "Generate engaging social media content for multiple platforms.",
    triggers: ["social post", "facebook post", "linkedin post", "twitter post", "instagram post", "threads", "schedule post", "post now", "publish now"],
    inputSchema: { 
      platform: "string?", 
      content: "string?", 
      when: "string?", 
      posts: "Array<{content:string; when:string}>?"
    },
    slots: [
      { name: "platform", prompt: "Which platform? (LinkedIn/Twitter/Instagram)", required: false },
      { name: "content", prompt: "What should the post say?", required: true },
      { name: "when", prompt: "When should I publish it? Say 'now' or provide a date/time.", required: false },
    ],
    handler: async (input: any) => {
      // Minimal runtime validation; BusinessConsultant will parse message into this shape
      const platform = (input?.platform || 'linkedin').toLowerCase();
      if (platform !== 'linkedin') throw new Error('Only LinkedIn is supported right now');

      // Batch: keep behavior, but pass media through when present
      if (Array.isArray(input?.posts) && input.posts.length > 1) {
        return socialSchedulerService.scheduleMultipleLinkedInPosts(
          input.posts.map((p: any) => ({
            content: String(p.content || ''),
            when: String(p.when || ''),
            media_urls: Array.isArray(p?.media_urls) ? p.media_urls : undefined,
            image_alt: typeof p?.image_alt === 'string' ? p.image_alt : undefined,
          }))
        );
      }

      const content = String(input?.content || '');
      if (!content) throw new Error('Content is required');

      // Normalize "when" with a resilient check for NOW semantics
      const rawWhen = (input?.when ?? 'now');
      const when = (typeof rawWhen === 'string' ? rawWhen : String(rawWhen)).trim();
      const whenLower = when.toLowerCase();
      const isNow = whenLower === 'now' || /\bpost\s*now\b|\bpublish\s*now\b|\bnow\b/.test(whenLower);

      const media_urls = Array.isArray(input?.media_urls) ? input.media_urls : undefined;
      const image_alt = typeof input?.image_alt === 'string' ? input.image_alt : undefined;

      // Draft-first: always create a draft so the user can preview/edit if posting fails
      const planner = new SocialMediaPlannerService();
      // Only set scheduled_for on the draft if we have a valid datetime we can parse to ISO
      let scheduledForISOForDraft: string | undefined = undefined;
      if (!isNow) {
        const t = Date.parse(when);
        if (!Number.isNaN(t)) scheduledForISOForDraft = new Date(t).toISOString();
      }
      const draft = await planner.createPost({
        content,
        media_urls: media_urls || [],
        platforms: ['linkedin'],
        scheduled_for: scheduledForISOForDraft,
        status: 'draft',
        platform_details: image_alt ? [{ platform: 'linkedin', image_alt, platform_specific_content: undefined, link_preview_enabled: true }] : []
      } as any);

      // Option A UX: Stop after draft creation and ask user to Post Now or Schedule
      // We return metadata so the chat layer can render CTAs without attempting publish/schedule here
      return {
        draftId: draft.id,
        status: 'draft',
        message: 'Draft created. Would you like to post now or schedule?',
        supportedActions: ['post_now', 'schedule'],
        // Echo back any parsed time for convenience, but do not act on it here
        when: isNow ? 'now' : scheduledForISOForDraft || undefined,
      };
    },
    route: "/marketing/social-media-planner"
  },

  // Marketing: Social Media Planner
  {
    id: "marketing.social_planner",
    name: "Social Media Planner",
    description: "Plan, schedule, and publish posts across platforms.",
    triggers: ["social planner", "schedule posts", "content calendar", "one-click publish"],
    inputSchema: { cadencePerWeek: "number?", channels: "string[]?" },
    slots: [
      { name: "cadencePerWeek", prompt: "How many posts per week would you like to plan?", required: false },
      { name: "channels", prompt: "Which channels? (comma-separated)", required: false },
    ],
    route: "/marketing/social-media-planner"
  },

  // Marketing: Press Release Generator
  {
    id: "marketing.press_release",
    name: "Press Release Generator",
    description: "Generate professional press releases for announcements.",
    triggers: ["press release", "PR", "announcement", "media"],
    inputSchema: { news: "string", quotes: "string[]?", targetMedia: "string[]?" },
    slots: [
      { name: "news", prompt: "What news are we announcing?", required: true },
      { name: "quotes", prompt: "Any quotes to include? (comma-separated)", required: false },
      { name: "targetMedia", prompt: "Any target media outlets? (comma-separated)", required: false },
    ],
    route: "/marketing/press-release-generator"
  },

  // Marketing: SEO Content Hub (unified blog + topical authority)
  {
    id: "marketing.seo_content_hub",
    name: "SEO Content Hub",
    description: "Complete SEO workflow: generate topical authority maps, create optimized blog posts with keyword control, and manage your content library.",
    triggers: ["blog post", "article", "content brief", "outline", "blog idea", "topical authority", "pillar articles", "topic clusters", "seo content"],
    inputSchema: { topic: "string", keywords: "string[]?", length: "string?", tone: "string?" },
    slots: [
      { name: "topic", prompt: "What topic should the blog cover?", required: true },
      { name: "keywords", prompt: "Any keywords to target? (comma-separated)", required: false },
      { name: "tone", prompt: "Preferred tone (e.g., professional, friendly)?", required: false },
      { name: "length", prompt: "Desired length (e.g., short, medium, long)?", required: false },
    ],
    route: "/marketing/seo-content-hub"
  },

  // Marketing: Business Launchpad
  {
    id: "marketing.launchpad",
    name: "Business Launchpad",
    description: "Plan and track your business launch with tasks and milestones.",
    triggers: ["launch plan", "go to market", "gtm", "milestones", "launch checklist"],
    inputSchema: { product: "string", targetMarket: "string?" },
    route: "/launchpad"
  },

  // Marketing: Print-on-Demand Designer
  {
    id: "marketing.pod_designer",
    name: "Print-on-Demand Designer",
    description: "Create custom designs for t-shirts, posters, and more.",
    triggers: ["print on demand", "t-shirt design", "merch", "mockups", "pod"],
    inputSchema: { prompt: "string", productTypes: "string[]?" },
    route: "/marketing/print-on-demand"
  },

  // Marketing: Text to Video Generator
  {
    id: "marketing.text_to_video",
    name: "Text to Video Generator",
    description: "Turn scripts into marketing videos with AI.",
    triggers: ["text to video", "ai video", "script to video", "shorts", "reels"],
    inputSchema: { script: "string", aspect: "string?", duration: "string?" },
    route: "/marketing/text-to-video-generator"
  },

  // Marketing: Cold Email Tool
  {
    id: "marketing.cold_email",
    name: "Cold Email Tool",
    description: "Create personalized cold emails and follow-ups.",
    triggers: ["cold email", "outreach", "prospect", "personalize", "follow up"],
    inputSchema: { audience: "string", offer: "string", steps: "number?", tone: "string?" },
    route: "/email"
  },

  // Marketing: Cold Email Campaign Creator (from chat)
  {
    id: "marketing.cold_email.create_campaign",
    name: "Cold Email Campaign Creator",
    description: "Create and launch cold email campaigns with AI-generated copy",
    triggers: [
      "create campaign",
      "launch campaign",
      "start campaign",
      "cold email campaign",
      "email sequence",
      "outreach campaign",
      "create cold email",
      "launch outreach"
    ],
    inputSchema: {
      campaign_name: "string",
      target_audience: "string",
      value_proposition: "string",
      tone: "string?"
    },
    slots: [
      { name: "campaign_name", prompt: "What should we call this campaign?", required: true },
      { name: "target_audience", prompt: "Who are you targeting? (e.g., 'B2B SaaS founders in healthcare')", required: true },
      { name: "value_proposition", prompt: "What's your value proposition or offer?", required: true },
      { name: "tone", prompt: "Preferred tone? (professional/friendly/direct - default: professional)", required: false }
    ],
    handler: async (input: { campaign_name: string; target_audience: string; value_proposition: string; tone?: string }) => {
      const aiEmailService = (await import('../services/aiEmailService')).default;
      const { supabase } = await import('../config/supabaseClient');
      const { API_BASE_URL } = await import('../config');

      // Get user auth
      const sessionResp = await supabase.auth.getSession();
      const token = (sessionResp as any)?.data?.session?.access_token || '';
      const userResp = await supabase.auth.getUser();
      const userId = (userResp as any)?.data?.user?.id;
      if (!userId) throw new Error('Authentication required');

      // Generate AI email copy
      const emailResult = await aiEmailService.generateEmail({
        purpose: `Cold email outreach campaign: ${input.campaign_name}`,
        targetAudience: input.target_audience,
        companyInfo: input.value_proposition,
        tone: (input.tone as any) || 'professional',
        length: 'short',
        includeCallToAction: true,
        avoidSpamTriggers: true
      });

      // Create campaign via API
      const apiUrl = `${API_BASE_URL}/api/email/campaigns`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: input.campaign_name,
          subject: emailResult.subject,
          body: emailResult.body,
          status: 'draft',
          targetAudience: input.target_audience,
          userId
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create campaign: ${errorText}`);
      }

      const campaign = await response.json();

      return {
        success: true,
        campaignId: campaign.id || campaign.data?.id,
        campaignName: input.campaign_name,
        subject: emailResult.subject,
        body: emailResult.body,
        variations: emailResult.variations,
        spamScore: emailResult.deliverability_analysis.spam_score,
        message: `Campaign "${input.campaign_name}" created! Review and edit in the Cold Email dashboard.`,
        route: `/email/campaigns/${campaign.id || campaign.data?.id}`,
        nextSteps: [
          'Review and edit the generated email copy',
          'Add your contacts or import a list',
          'Configure your sending schedule',
          'Activate the campaign when ready'
        ]
      };
    },
    route: "/email"
  },

  {
    id: "crm.telemarketing.book_meetings_campaign",
    name: "AI Telemarketing (Book Meetings)",
    description: "Create an AI telemarketing campaign to book meetings using the Explanatum methodology.",
    triggers: [
      "telemarketing",
      "cold calling",
      "cold call",
      "outbound calling",
      "call prospects",
      "ai caller",
      "book meetings",
      "book demos",
      "schedule calls",
      "generate pipeline"
    ],
    inputSchema: {
      campaign_name: "string",
      company_name: "string",
      value_proposition: "string",
      timezone: "string?",
    },
    slots: [
      { name: "campaign_name", prompt: "What should we call this telemarketing campaign?", required: true },
      { name: "company_name", prompt: "What company name should the AI represent on calls?", required: true },
      { name: "value_proposition", prompt: "In one sentence: We help businesses like yours ___ (what outcome)?", required: true },
    ],
    handler: async (input: { campaign_name: string; company_name: string; value_proposition: string; timezone?: string }) => {
      const campaignName = String(input?.campaign_name || '').trim();
      const companyName = String(input?.company_name || '').trim();
      const valueProposition = String(input?.value_proposition || '').trim();

      if (!campaignName) throw new Error('campaign_name is required');
      if (!companyName) throw new Error('company_name is required');
      if (!valueProposition) throw new Error('value_proposition is required');

      let organizationId = String((useCrmOrgStore.getState() as any)?.activeOrgId || '').trim();
      if (!organizationId) {
        const response = await organizationsService.list();
        const list = (response as any)?.data || [];
        if (Array.isArray(list) && list.length === 1 && list[0]?.id) {
          organizationId = String(list[0].id);
          useCrmOrgStore.getState().setActiveOrgId(organizationId);
        }
      }

      if (!organizationId) {
        throw new Error('Select an active CRM workspace first (CRM → Telemarketing), then try again.');
      }

      const tz = String(input?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');

      let bookingLinksCount = 0;
      try {
        const links = await schedulingService.getBookingLinks(organizationId, false);
        bookingLinksCount = Array.isArray(links) ? links.length : 0;
      } catch {
        bookingLinksCount = 0;
      }

      const campaign = await telemarketingService.createCampaign({
        organization_id: organizationId,
        name: campaignName,
        description: `Book meetings for ${companyName}`,
        objective: 'book_demo',
        ai_agent_personality: 'consultative',
        ai_agent_name: 'Alex',
        company_name: companyName,
        value_proposition: valueProposition,
        use_explanatum_methodology: true,
        methodology_config: {
          name: 'Explanatum_B2B_Methodology',
          version: '1.0.0',
          agent_name: 'Alex',
          company_name: companyName,
          value_proposition: valueProposition,
          enabled_stages: EXPLANATUM_ENABLED_STAGES
        },
        call_schedule: {
          days: ['mon', 'tue', 'wed', 'thu', 'fri'],
          hours: { start: '09:00', end: '17:00' },
          timezone: tz
        },
        status: 'draft'
      } as any);

      if (!campaign?.id) {
        throw new Error('Campaign created but no id was returned');
      }

      const bookingNote = bookingLinksCount > 0
        ? ''
        : ' Before starting, set up a booking link in CRM → Scheduling so prospects can pick a time.';

      return {
        type: 'tool-cta',
        toolId: 'crm.telemarketing.book_meetings_campaign',
        text: `Telemarketing campaign "${campaignName}" created as a draft.${bookingNote} Next: add/upload contacts, then click Start.`,
        route: `/crm/telemarketing/campaigns/${campaign.id}`,
        ctaLabel: 'Open Campaign'
      };
    },
    route: "/crm/telemarketing"
  },

  // Marketing: Affiliate Management
  {
    id: "marketing.affiliate_mgmt",
    name: "Affiliate Management",
    description: "Set up and manage your affiliate program.",
    triggers: ["affiliate", "referrals", "commissions", "signup form", "payouts"],
    inputSchema: { commissionRate: "string?", terms: "string?" },
    route: "/marketing/affiliate-management"
  },

  // Marketing: SEO Analyzer
  {
    id: "marketing.seo_analyzer",
    name: "SEO Analyzer",
    description: "Analyze SEO performance and get actionable insights.",
    triggers: ["seo", "audit", "on-page", "technical seo", "site speed", "core web vitals"],
    inputSchema: { domain: "string", focusPages: "string[]?" },
    slots: [
      { name: "domain", prompt: "What website should I analyze? Please provide the full URL.", required: true },
      { name: "focusPages", prompt: "Any specific pages to focus on? (comma-separated URLs)", required: false },
    ],
    route: "/marketing/seo-analyzer"
  },

  // Video Recorder removed - non-functional

  // Marketing: Web Traffic Analytics
  {
    id: "marketing.web_analytics",
    name: "Web Traffic Analytics",
    description: "Analyze search performance across Google and Bing.",
    triggers: ["analytics", "google search console", "bing", "rankings", "impressions", "clicks"],
    inputSchema: { sources: "string[]?" },
    route: "/marketing/web-traffic-analytics"
  },


  // AI Website Generator removed - non-functional

  // Marketing: Inbound Lead Generation Playbooks
  {
    id: "marketing.leadgen_playbooks",
    name: "Inbound Lead Gen Playbooks",
    description: "Plan inbound campaigns across SEO, content, lead magnets and nurture.",
    triggers: [
      "inbound",
      "leads",
      "lead gen",
      "lead generation",
      "more leads",
      "increase leads",
      "get more leads",
      "pipeline",
      "mql",
      "seo",
      "content marketing",
      "landing page",
      "conversion rate",
      "organic traffic"
    ],
    // Handler omitted: UI flow collects inputs for personalization
    route: "/marketing"
  },

  // Finance (one entry per module)
  {
    id: "finance.price_calculator",
    name: "Price Calculator",
    description: "Create and manage pricing scenarios.",
    triggers: ["price calculator", "pricing scenario", "set price"],
    inputSchema: { scenario: "PricingScenario" },
    handler: async (input: { scenario: any }) => pricingScenarioService.create(input.scenario),
    route: "/finance/price-calculator"
  },
  {
    id: "finance.profit_margin",
    name: "Profit Margin Analyzer",
    description: "Create and analyze profit margin records.",
    triggers: ["profit margin", "margin", "gross margin"],
    inputSchema: { analysis: "ProfitMarginAnalysis" },
    handler: async (input: { analysis: any }) => profitMarginService.create(input.analysis),
    route: "/finance/profit-margin"
  },
  {
    id: "finance.breakeven",
    name: "Break-Even Calculator",
    description: "Create and analyze break-even calculations.",
    triggers: ["break even", "breakeven", "break-even"],
    inputSchema: { analysis: "BreakevenAnalysis" },
    handler: async (input: { analysis: any }) => breakevenService.create(input.analysis),
    route: "/finance/break-even"
  },
  {
    id: "finance.cash_flow",
    name: "Cash Flow Forecaster",
    description: "Create and update cash flow projections.",
    triggers: ["cash flow", "cashflow", "projection"],
    inputSchema: { projection: "CashFlowProjection", items: "CashFlowProjectionItem[]" },
    handler: async (input: { projection: any; items: any[] }) => cashFlowService.create(input.projection, input.items),
    route: "/finance/cash-flow"
  },
  {
    id: "finance.pricing_strategy",
    name: "Pricing Strategy Advisor",
    description: "Create and manage pricing strategies.",
    triggers: ["pricing strategy", "strategy price", "value-based pricing"],
    inputSchema: { strategy: "PricingStrategy" },
    handler: async (input: { strategy: any }) => pricingStrategyService.create(input.strategy),
    route: "/finance/pricing-strategy"
  },
  {
    id: "finance.sales_tracker",
    name: "Sales Tracker",
    description: "Record daily sales and view recent transactions.",
    triggers: ["sales tracker", "add sales", "daily sales", "record sale"],
    inputSchema: { date: "string", amount: "number", product: "string?", channel: "string?" },
    // CTA-only; use UI for correct payload and auth
    route: "/finance/sales-tracker"
  },
  {
    id: "finance.expense_tracker",
    name: "Expense Tracker",
    description: "Record expenses, categorize costs, and review recent spending.",
    triggers: ["expense tracker", "add expense", "record cost", "spending"],
    inputSchema: { date: "string", amount: "number", category: "string?", vendor: "string?" },
    // CTA-only; use UI for correct payload and auth
    route: "/finance/expense-tracker"
  },

  // Operations
  {
    id: "operations.supplier_finder",
    name: "Supplier Finder",
    description: "Find licensed trade suppliers.",
    triggers: ["supplier", "find supplier", "wholesaler", "trade supplier"],
    inputSchema: { product: "string", location: "string" },
    // Handler intentionally omitted: requires userId; use route CTA to collect info in UI
    route: "/finance/supplier-finder"
  },
  {
    id: "operations.risk_manager",
    name: "Risk Manager",
    description: "Identify and assess business risks.",
    triggers: ["risk", "risk register", "risk assessment"],
    inputSchema: { title: "string", description: "string" },
    // Handler intentionally omitted: invoked via UI flow for correct payload & auth
    route: "/finance/risk-manager"
  },
];

const toolsRegistry: ToolEntry[] = [...toolsRegistryBase];

if (CRM_ADVISOR_TOOLS_ENABLED) {
  toolsRegistry.push(
    {
      id: "crm.pipeline_snapshot",
      name: "CRM Pipeline Snapshot",
      description: "Summarize weighted pipeline and forecast metrics for advisor insights.",
      triggers: ["pipeline snapshot", "forecast summary", "crm forecast", "pipeline health"],
      preconditions: ["auth.required", "plan.premium"],
      inputSchema: {
        organization_id: "string",
        rangeDays: { type: "number", enum: [7, 30, 90], optional: true },
        onlyMine: "boolean?"
      },
      handler: async (input: { organization_id?: string; rangeDays?: 7 | 30 | 90; onlyMine?: boolean }) => {
        const organization_id = String(input?.organization_id || "").trim();
        if (!organization_id) throw new Error("organization_id is required");
        const summary = await fetchDashboardSummary(organization_id, {
          rangeDays: input?.rangeDays,
          onlyMine: input?.onlyMine
        });
        return buildAdvisorDashboardSnapshot(summary);
      }
    },
    {
      id: "crm.at_risk_deals",
      name: "CRM At-Risk Deals",
      description: "Identify deals that are stale or below probability thresholds.",
      triggers: ["at risk deals", "deal risks", "pipeline risks", "stale deals"],
      preconditions: ["auth.required", "plan.premium"],
      inputSchema: {
        organization_id: "string",
        probabilityThreshold: "number?",
        staleDays: "number?",
        limit: "number?"
      },
      handler: async (input: { organization_id?: string; probabilityThreshold?: number; staleDays?: number; limit?: number }) => {
        const organization_id = String(input?.organization_id || "").trim();
        if (!organization_id) throw new Error("organization_id is required");
        const limit = typeof input?.limit === "number" && input.limit > 0 ? input.limit : 100;
        const { data } = await dealsService.list({ organization_id, limit });
        const risks = getAtRiskDeals(data || [], {
          probabilityThreshold: input?.probabilityThreshold,
          staleDays: input?.staleDays
        });
        return { items: risks };
      }
    },
    {
      id: "crm.data_quality_scores",
      name: "CRM Data Quality Scores",
      description: "Assess enrichment completeness for contacts and companies.",
      triggers: ["data quality", "enrichment score", "contact hygiene", "account hygiene"],
      preconditions: ["auth.required", "plan.premium"],
      inputSchema: {
        organization_id: "string",
        contactLimit: "number?",
        companyLimit: "number?"
      },
      handler: async (input: { organization_id?: string; contactLimit?: number; companyLimit?: number }) => {
        const organization_id = String(input?.organization_id || "").trim();
        if (!organization_id) throw new Error("organization_id is required");
        const contactLimit = typeof input?.contactLimit === "number" && input.contactLimit > 0 ? input.contactLimit : 100;
        const companyLimit = typeof input?.companyLimit === "number" && input.companyLimit > 0 ? input.companyLimit : 100;

        const [contactsRes, companiesRes] = await Promise.all([
          contactsService.list({ organization_id, limit: contactLimit }),
          companiesService.list({ organization_id, limit: companyLimit })
        ]);

        return {
          contacts: buildContactDataQuality(contactsRes?.data),
          companies: buildCompanyDataQuality(companiesRes?.data)
        };
      }
    }
  );
}

export type ToolId = typeof toolsRegistry[number]["id"];

export function getToolById(id: string): ToolEntry | undefined {
  return toolsRegistry.find((tool) => tool.id === id);
}

export { toolsRegistry };
